import Swal from "sweetalert2";
import moment from "moment-timezone";
export const handleCreateTransfer = async ({
  transferPricing,
  setBasePrice,
  contact,
  fetchData,
  cars,
  rentals,
  coupons,
  drivers,
  tours,
  tourBookings,
  employeeService,
  setCarsData,
  setRentalsData,
  setCouponsData,
  setTransfersData,
  setDriversData,
  setToursData,
  setTourBookingsData,
  setEmployeesData,
  setSeasonalPricing,
  setIsLoading,
  setError,
}) => {
  try {
    // Get vehicle type options
    const vehicleTypeOptions = [
      { value: "car", label: "Car" },
      { value: "minivan", label: "Mini Van" },
      { value: "van", label: "Van" },
      { value: "minibus", label: "Mini Bus" },
    ];

    // Get location options from transfer pricing data
    const locationOptions = [];
    const transferPricings = await transferPricing.getAll();
    if (transferPricings && transferPricings.data) {
      // Extract unique pickup and dropoff locations
      const uniqueFromLocations = new Set();
      const uniqueToLocations = new Set();

      transferPricings.data.forEach((pricing) => {
        uniqueFromLocations.add(pricing.fromLocation);
        uniqueToLocations.add(pricing.toLocation);
      });

      // Convert to options format
      uniqueFromLocations.forEach((location) => {
        locationOptions.push({ value: location, label: location });
      });
      uniqueToLocations.forEach((location) => {
        if (!locationOptions.some((opt) => opt.value === location)) {
          locationOptions.push({ value: location, label: location });
        }
      });
    }

    // Get season for date calculation
    const getSeason = (date) => {
      const month = date.getMonth() + 1; // JavaScript months are 0-based
      const day = date.getDate();

      // Low Season: April 1 - June 25, September 16 - October 30
      if (
        (month === 4 && day >= 1) ||
        month === 5 ||
        (month === 6 && day <= 25) ||
        (month === 9 && day >= 16) ||
        (month === 10 && day <= 30)
      ) {
        return "low";
      }
      // Medium Season: June 26 - July 10, August 26 - September 15
      else if (
        (month === 6 && day >= 26) ||
        (month === 7 && day <= 10) ||
        (month === 8 && day >= 26) ||
        (month === 9 && day <= 15)
      ) {
        return "medium";
      }
      // High Season: July 11 - August 25
      else if ((month === 7 && day >= 11) || (month === 8 && day <= 25)) {
        return "high";
      }
      // Default to medium season
      else {
        return "medium";
      }
    };

    // Calculate price based on route and season
    const calculateTransferPrice = (
      fromLocation,
      toLocation,
      season,
      carType
    ) => {
      // Find the pricing record for this route
      const pricingRecord = transferPricings.data.find(
        (pricing) =>
          (pricing.fromLocation === fromLocation &&
            pricing.toLocation === toLocation) ||
          (pricing.fromLocation === toLocation &&
            pricing.toLocation === fromLocation)
      );

      if (!pricingRecord) return null;

      let price = 0;
      switch (season) {
        case "low":
          price = pricingRecord.lowSeasonPrice;
          break;
        case "medium":
          price = pricingRecord.mediumSeasonPrice;
          break;
        case "high":
          price = pricingRecord.highSeasonPrice;
          break;
        default:
          price = pricingRecord.mediumSeasonPrice;
      }
      setBasePrice(price);
      return Math.round(price);
    };

    const { value: formValues, dismiss: transferDismiss } = await Swal.fire({
      title: "Create New Transfer",
      html: `
          <div class="grid grid-cols-1 gap-4 max-h-[70vh] overflow-y-auto p-2">
            <!-- Customer Information -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Customer Information</h3>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="name" class="block text-sm font-medium text-left mb-1">First Name*</label>
                  <input id="name" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="surname" class="block text-sm font-medium text-left mb-1">Last Name*</label>
                  <input id="surname" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="email" class="block text-sm font-medium text-left mb-1">Email*</label>
                  <input id="email" type="email" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="phone" class="block text-sm font-medium text-left mb-1">Phone*</label>
                  <input id="phone" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
            </div>
            
            <!-- Transfer Details -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Transfer Details</h3>
              <div class="mb-3">
                <label for="carType" class="block text-sm font-medium text-left mb-1">Vehicle Type*</label>
                <select id="carType" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select vehicle type</option>
                  ${vehicleTypeOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                </select>
              </div>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="persons" class="block text-sm font-medium text-left mb-1">Passengers*</label>
                  <input id="persons" type="number" min="1" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="luggage" class="block text-sm font-medium text-left mb-1">Luggage*</label>
                  <input id="luggage" type="number" min="0" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              <div class="mb-3">
                <label for="serviceType" class="block text-sm font-medium text-left mb-1">Service Type*</label>
                <select id="serviceType" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="one-way">One Way</option>
                  <option value="round-trip">Round Trip</option>
                </select>
              </div>
            </div>
            
            <!-- Locations and Dates -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Locations and Dates</h3>
              <div class="mb-3">
                <label for="pickupLocation" class="block text-sm font-medium text-left mb-1">Pickup Location*</label>
                <select id="pickupLocation" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select pickup location</option>
                  ${locationOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                  <option value="other">Other (Custom)</option>
                </select>
              </div>
              <div id="otherPickupContainer" class="mb-3 hidden">
                <label for="otherPickupLocation" class="block text-sm font-medium text-left mb-1">Custom Pickup Location*</label>
                <input id="otherPickupLocation" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              
              <div class="mb-3">
                <label for="dropoffLocation" class="block text-sm font-medium text-left mb-1">Drop-off Location*</label>
                <select id="dropoffLocation" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select drop-off location</option>
                  ${locationOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                  <option value="other">Other (Custom)</option>
                </select>
              </div>
              <div id="otherDropoffContainer" class="mb-3 hidden">
                <label for="otherDropoffLocation" class="block text-sm font-medium text-left mb-1">Custom Drop-off Location*</label>
                <input id="otherDropoffLocation" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="pickupDate" class="block text-sm font-medium text-left mb-1">Pickup Date*</label>
                  <input id="pickupDate" type="date" class="w-full px-3 py-2 border rounded-md" min="${moment().format(
                    "YYYY-MM-DD"
                  )}" required>
                </div>
                <div>
                  <label for="pickupTime" class="block text-sm font-medium text-left mb-1">Pickup Time*</label>
                  <input id="pickupTime" type="time" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              
              <div id="returnDetailsContainer" class="hidden">
                <div class="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label for="returnDate" class="block text-sm font-medium text-left mb-1">Return Date*</label>
                    <input id="returnDate" type="date" class="w-full px-3 py-2 border rounded-md" min="${moment().format(
                      "YYYY-MM-DD"
                    )}">
                  </div>
                  <div>
                    <label for="returnTime" class="block text-sm font-medium text-left mb-1">Return Time*</label>
                    <input id="returnTime" type="time" class="w-full px-3 py-2 border rounded-md">
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Additional Information -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Additional Options</h3>
              <div class="mb-3">
                <label for="paymentMethod" class="block text-sm font-medium text-left mb-1">Payment Method*</label>
                <select id="paymentMethod" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="credit-card">Credit Card</option>
                  <option value="cash">Cash</option>
                  <option value="bank-transfer">Bank Transfer</option>
                </select>
              </div>
              <div class="mb-3">
                <label for="airportTicketId" class="block text-sm font-medium text-left mb-1">Flight/Ferry Number</label>
                <input id="airportTicketId" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              <div class="mb-3">
                <div class="flex items-center">
                  <input id="atDisposal" type="checkbox" class="mr-2">
                  <label for="atDisposal" class="text-sm font-medium">Driver at Disposal</label>
                </div>
              </div>
              <div id="disposalHoursContainer" class="mb-3 hidden">
                <label for="disposalHours" class="block text-sm font-medium text-left mb-1">Disposal Hours</label>
                <select id="disposalHours" class="w-full px-3 py-2 border rounded-md">
                  ${Array.from({ length: 12 }, (_, i) => i + 1)
                    .map(
                      (h) =>
                        `<option value="${h}">${h} hour${
                          h > 1 ? "s" : ""
                        }</option>`
                    )
                    .join("")}
                </select>
              </div>
              <div class="mb-3">
                <div class="flex items-center">
                  <input id="helicopterTransfer" type="checkbox" class="mr-2">
                  <label for="helicopterTransfer" class="text-sm font-medium">Helicopter Transfer</label>
                </div>
              </div>
              <div class="mb-3">
                <label for="additionalInfo" class="block text-sm font-medium text-left mb-1">Additional Comments</label>
                <textarea id="additionalInfo" class="w-full px-3 py-2 border rounded-md" rows="3"></textarea>
              </div>
            </div>
          </div>
        `,
      didOpen: () => {
        // Add event listeners for conditional fields
        document
          .getElementById("serviceType")
          .addEventListener("change", function () {
            document.getElementById("returnDetailsContainer").style.display =
              this.value === "round-trip" ? "block" : "none";
          });

        document
          .getElementById("pickupLocation")
          .addEventListener("change", function () {
            document.getElementById("otherPickupContainer").style.display =
              this.value === "other" ? "block" : "none";

            // Update pricing
            updatePriceEstimate();
          });

        document
          .getElementById("dropoffLocation")
          .addEventListener("change", function () {
            document.getElementById("otherDropoffContainer").style.display =
              this.value === "other" ? "block" : "none";

            // Update pricing
            updatePriceEstimate();
          });

        document
          .getElementById("carType")
          .addEventListener("change", function () {
            // Update pricing
            updatePriceEstimate();
          });

        document
          .getElementById("pickupDate")
          .addEventListener("change", function () {
            // Update pricing
            updatePriceEstimate();
          });

        document
          .getElementById("atDisposal")
          .addEventListener("change", function () {
            document.getElementById("disposalHoursContainer").style.display =
              this.checked ? "block" : "none";
          });

        // Function to update price estimate
        function updatePriceEstimate() {
          const pickupLocation =
            document.getElementById("pickupLocation").value;
          const dropoffLocation =
            document.getElementById("dropoffLocation").value;
          const pickupDate = document.getElementById("pickupDate").value;
          const carType = document.getElementById("carType").value;

          // Only calculate if all fields are filled
          if (
            pickupLocation &&
            pickupLocation !== "other" &&
            dropoffLocation &&
            dropoffLocation !== "other" &&
            pickupDate &&
            carType
          ) {
            const season = getSeason(new Date(pickupDate));
            const price = calculateTransferPrice(
              pickupLocation,
              dropoffLocation,
              season,
              carType
            );

            if (price) {
              // If a price estimate exists, display it somewhere
              let priceEstimateEl = document.getElementById("priceEstimate");
              if (!priceEstimateEl) {
                priceEstimateEl = document.createElement("div");
                priceEstimateEl.id = "priceEstimate";
                priceEstimateEl.className =
                  "mt-4 p-3 bg-blue-50 text-blue-800 rounded-md";
                document
                  .querySelector(".swal2-html-container")
                  .appendChild(priceEstimateEl);
              }

              priceEstimateEl.innerHTML = `
                  <p class="font-medium">Estimated Price: €${price}</p>
                  <p class="text-xs">${
                    season.charAt(0).toUpperCase() + season.slice(1)
                  } Season Rate</p>
                `;
            }
          }
        }
      },
      showCancelButton: true,
      confirmButtonText: "Create Transfer",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1e3a8a",
      width: "800px",
      preConfirm: () => {
        // Get form values
        const name = document.getElementById("name").value;
        const surname = document.getElementById("surname").value;
        const email = document.getElementById("email").value;
        const phone = document.getElementById("phone").value;
        const carType = document.getElementById("carType").value;
        const persons = document.getElementById("persons").value;
        const luggage = document.getElementById("luggage").value;
        const serviceType = document.getElementById("serviceType").value;
        const pickupLocation = document.getElementById("pickupLocation").value;
        const otherPickupLocation = document.getElementById(
          "otherPickupLocation"
        ).value;
        const dropoffLocation =
          document.getElementById("dropoffLocation").value;
        const otherDropoffLocation = document.getElementById(
          "otherDropoffLocation"
        ).value;
        const pickupDate = document.getElementById("pickupDate").value;
        const pickupTime = document.getElementById("pickupTime").value;
        const returnDate = document.getElementById("returnDate")?.value;
        const returnTime = document.getElementById("returnTime")?.value;
        const paymentMethod = document.getElementById("paymentMethod").value;
        const airportTicketId =
          document.getElementById("airportTicketId").value;
        const atDisposal = document.getElementById("atDisposal").checked;
        const disposalHours = document.getElementById("disposalHours").value;
        const helicopterTransfer =
          document.getElementById("helicopterTransfer").checked;
        const additionalInfo = document.getElementById("additionalInfo").value;

        // Validate required fields
        if (
          !name ||
          !surname ||
          !email ||
          !phone ||
          !carType ||
          !persons ||
          !luggage ||
          !pickupLocation ||
          !dropoffLocation ||
          !pickupDate ||
          !pickupTime
        ) {
          Swal.showValidationMessage("Please fill all required fields");
          return false;
        }

        if (pickupLocation === "other" && !otherPickupLocation) {
          Swal.showValidationMessage("Please enter a custom pickup location");
          return false;
        }

        if (dropoffLocation === "other" && !otherDropoffLocation) {
          Swal.showValidationMessage("Please enter a custom drop-off location");
          return false;
        }

        if (serviceType === "round-trip" && (!returnDate || !returnTime)) {
          Swal.showValidationMessage("Please enter return date and time");
          return false;
        }

        // Parse dates and times
        const [pickupHours, pickupMinutes] = pickupTime.split(":");
        const pickUpDateObj = new Date(pickupDate);
        pickUpDateObj.setHours(parseInt(pickupHours), parseInt(pickupMinutes));

        let returnDateObj = null;
        let returnHours = null;
        let returnMinutes = null;

        if (serviceType === "round-trip" && returnDate && returnTime) {
          [returnHours, returnMinutes] = returnTime.split(":");
          returnDateObj = new Date(returnDate);
          returnDateObj.setHours(
            parseInt(returnHours),
            parseInt(returnMinutes)
          );

          if (returnDateObj < pickUpDateObj) {
            Swal.showValidationMessage(
              "Return date cannot be before pickup date"
            );
            return false;
          }
        }

        // Calculate season based on date
        const season = getSeason(pickUpDateObj);

        // Calculate price if standard locations are selected
        let price = null;
        if (pickupLocation !== "other" && dropoffLocation !== "other") {
          price = calculateTransferPrice(
            pickupLocation,
            dropoffLocation,
            season,
            carType
          );
        }

        // Create final form data
        return {
          name,
          surname,
          email,
          phone,
          carType,
          persons,
          luggage,
          serviceType,
          pickupLocation:
            pickupLocation === "other" ? otherPickupLocation : pickupLocation,
          dropoffLocation:
            dropoffLocation === "other"
              ? otherDropoffLocation
              : dropoffLocation,
          pickUpDate: pickUpDateObj,
          pickupHours,
          pickupMinutes,
          dropOffDate: pickUpDateObj, // For one-way, drop-off date is same as pickup
          dropoffHours: pickupHours,
          dropoffMinutes: pickupMinutes,
          returnDate: returnDateObj,
          returnHours,
          returnMinutes,
          paymentMethod,
          airportTicketId,
          atDisposal,
          disposalHours: atDisposal ? disposalHours : null,
          helicopterTransfer,
          additionalInfo,
          status: "pending",
          isReturn: serviceType === "round-trip",
          seasonType: season,
          totalPrice: price,
        };
      },
    });

    if (transferDismiss) return;

    // Create the transfer
    await contact.submitTransferRequest(formValues);

    await Swal.fire({
      icon: "success",
      title: "Success",
      text: "Transfer created successfully",
      confirmButtonColor: "#1e3a8a",
    });

  fetchData(); // Refresh tour data
  } catch (error) {
    console.error("Error creating transfer:", error);
    await Swal.fire({
      icon: "error",
      title: "Error",
      text: "Failed to create transfer. Please try again.",
      confirmButtonColor: "#1e3a8a",
    });
  }
};
